// ── Profile ──────────────────────────────────────────────────
export type UserRole = 'USER' | 'HOST' | 'BOTH'

export interface Profile {
  id: string
  role: UserRole
  is_host: boolean
  is_verified: boolean | null
  host_status: 'PENDING_VERIFICATION' | 'VERIFIED' | 'REJECTED' | null
  host_insurance_declared: boolean | null
  host_insurance_declared_at: string | null
  renter_insurance_declared: boolean | null
  renter_insurance_declared_at: string | null
  full_name: string | null
  username: string | null
  email: string | null
  phone: string | null
  business_name: string | null
  business_company_name: string | null
  abn: string | null
  avatar_url: string | null
  suburb: string | null
  created_at: string
  updated_at: string | null
}

export function isHost(profile: Profile | null): boolean {
  if (!profile) return false
  return profile.is_host || profile.role === 'HOST' || profile.role === 'BOTH'
}

// ── Space ─────────────────────────────────────────────────────
export type SpaceType = 'warehouse' | 'workshop' | 'yard' | 'storage' | 'shed' | 'creative_space'
export type PowerType = 'singlePhase' | 'threePhase' | 'both' | 'none'
export type AccessHours = 'twentyFourSeven' | 'businessHours' | 'custom'
export type RentalDuration = 'daily' | 'weekly' | 'monthly'

export const SPACE_TYPE_LABELS: Record<SpaceType, string> = {
  warehouse: 'Warehouse Bay',
  workshop: 'Workshop',
  yard: 'Secure Yard',
  storage: 'Storage',
  shed: 'Shed',
  creative_space: 'Creative Space',
}

export const ACCESS_HOURS_LABELS: Record<AccessHours, string> = {
  twentyFourSeven: '24/7 Access',
  businessHours: 'Business Hours',
  custom: 'Custom Hours',
}

export const POWER_TYPE_LABELS: Record<PowerType, string> = {
  singlePhase: 'Single Phase',
  threePhase: 'Three Phase',
  both: 'Single & Three Phase',
  none: 'No Power',
}

export const RENTAL_DURATION_LABELS: Record<RentalDuration, string> = {
  daily: 'Daily',
  weekly: 'Weekly',
  monthly: 'Monthly',
}

export interface Equipment {
  id: string
  name: string
  description?: string
  pricePerDay: number
  requiresSafetyInduction: boolean
  safetyRequirements?: string
}

export interface Space {
  id: string
  host_id: string
  title: string
  description: string
  space_type: SpaceType
  suburb: string
  state: string
  postcode: string
  full_address?: string
  latitude: number
  longitude: number
  sqm: number
  ceiling_height?: number
  roller_door_height?: number
  roller_door_width?: number
  has_loading_dock: boolean
  power_type: PowerType
  access_hours: AccessHours
  custom_access_hours?: string
  vehicle_access_ute: boolean
  vehicle_access_van: boolean
  vehicle_access_truck: boolean
  forklift_access: boolean
  has_toilets: boolean
  has_wash_bay: boolean
  has_office_area: boolean
  has_internet: boolean
  has_cctv: boolean
  is_gated: boolean
  has_alarm: boolean
  insurance_required: boolean
  instant_book_enabled: boolean
  daily_rate: number
  weekly_rate?: number
  monthly_rate?: number
  hourly_rate?: number
  cleaning_fee?: number
  minimum_duration: RentalDuration
  equipment: Equipment[]
  rules: string[]
  average_rating: number
  total_reviews: number
  blocked_dates: string[]
  is_active: boolean
  is_featured: boolean
  host_insurance_declared?: boolean | null
  host_insurance_declared_at?: string | null
  created_at: string
  updated_at: string
  // Joined / computed fields
  imageUrls?: string[]
  host?: Pick<Profile, 'id' | 'full_name' | 'avatar_url' | 'business_name'>
}

export function spaceLocation(space: Space): string {
  return `${space.suburb}, ${space.state} ${space.postcode}`
}

export function spacePriceDisplay(space: Space): string {
  return `$${Math.round(space.daily_rate)}/day`
}

// ── Pricing ───────────────────────────────────────────────────
// weekly_rate / monthly_rate are PERCENT DISCOUNTS (0–99), not dollar amounts —
// the host forms label them "Weekly Discount (%)" and validate .min(0).max(99).
// Rows written before that switch hold dollar values (e.g. weekly_rate = 300),
// which read as a 300% discount and produced negative booking totals. Anything
// outside 0–99 is therefore treated as "no discount": it is never applied to a
// price and never rendered as a badge.
export function discountPct(value?: number | null): number | null {
  if (value == null || !Number.isFinite(value)) return null
  if (value <= 0 || value >= 100) return null
  return value
}

export interface AutoPricing {
  basePrice: number
  rateNote: string
  durationType: RentalDuration
}

// Pick the best rate for a date range: monthly discount from 30 days, weekly
// from 7, otherwise plain daily. `fallbackBasePrice` is what callers get when
// no dates are chosen yet (the detail page shows 0, the booking flow shows one
// day's rate).
export function computeAutoPricing(
  space: Space,
  startDate: string | undefined,
  endDate: string | undefined,
  fallbackBasePrice = 0
): AutoPricing {
  const fallback: AutoPricing = {
    basePrice: fallbackBasePrice,
    rateNote: '',
    durationType: 'daily',
  }

  if (!startDate || !endDate) return fallback

  const start = new Date(startDate)
  const end = new Date(endDate)
  const days = Math.max(1, Math.ceil((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24)))
  // An unparseable date yields NaN, which would otherwise render as "$NaN".
  if (!Number.isFinite(days)) return fallback

  const monthly = discountPct(space.monthly_rate)
  if (days >= 30 && monthly !== null) {
    return {
      basePrice: space.daily_rate * days * (1 - monthly / 100),
      rateNote: `Monthly discount (${monthly}%) applied`,
      durationType: 'monthly',
    }
  }

  const weekly = discountPct(space.weekly_rate)
  if (days >= 7 && weekly !== null) {
    return {
      basePrice: space.daily_rate * days * (1 - weekly / 100),
      rateNote: `Weekly discount (${weekly}%) applied`,
      durationType: 'weekly',
    }
  }

  return { basePrice: space.daily_rate * days, rateNote: '', durationType: 'daily' }
}

// ── Booking ───────────────────────────────────────────────────
export type BookingStatus = 'pending' | 'approved' | 'confirmed' | 'cancelled' | 'completed'

export const BOOKING_STATUS_LABELS: Record<BookingStatus, string> = {
  pending: 'Pending Approval',
  approved: 'Approved — Payment Required',
  confirmed: 'Confirmed',
  cancelled: 'Cancelled',
  completed: 'Completed',
}

export interface BookingAddOn {
  id: string
  name: string
  price: number
  quantity: number
}

export interface Booking {
  id: string
  space_id: string
  space_title: string
  space_image_url: string
  renter_id: string
  renter_name: string
  host_id: string
  host_name: string
  start_date: string
  end_date: string
  duration_type: RentalDuration
  number_of_people: number
  number_of_vehicles: number
  add_ons: BookingAddOn[]
  base_price: number
  add_ons_total: number
  cleaning_fee: number
  processing_fee: number
  total_price: number
  status: BookingStatus
  payment_status?: 'unpaid' | 'paid' | 'expired'
  stripe_checkout_session_id?: string
  stripe_payment_intent_id?: string
  cancellation_reason?: string
  access_instructions?: string
  insurance_confirmed: boolean
  rules_accepted: boolean
  renter_notes?: string
  created_at: string
  updated_at: string
}

// Treat anything negative or non-finite as zero, so a corrupt input can never
// turn into a negative charge. This runs on the persist path too
// (bookingService.createBooking), not just on screen.
function nonNegative(value: number): number {
  return Number.isFinite(value) && value > 0 ? value : 0
}

export function calculateBookingFees(basePrice: number, addOnsTotal: number, cleaningFee: number): {
  serviceFee: number
  totalPrice: number
} {
  const base = nonNegative(basePrice)
  const addOns = nonNegative(addOnsTotal)
  const cleaning = nonNegative(cleaningFee)
  const serviceFee = (base + addOns) * 0.17 // 17%
  const totalPrice = base + addOns + cleaning + serviceFee
  return { serviceFee, totalPrice }
}

// ── Conversation & Message ────────────────────────────────────
export interface Conversation {
  id: string
  space_id: string
  space_title: string
  space_image_url: string
  renter_id: string
  renter_name: string
  renter_avatar_url?: string
  host_id: string
  host_name: string
  host_avatar_url?: string
  last_message?: string
  last_message_at?: string
  unread_count: number
  created_at: string
  updated_at: string
}

export interface Message {
  id: string
  conversation_id: string
  sender_id: string
  sender_name: string
  sender_avatar_url?: string
  content: string
  image_url?: string
  document_url?: string
  document_name?: string
  is_read: boolean
  created_at: string
}

// ── Review ────────────────────────────────────────────────────
export interface Review {
  id: string
  space_id: string
  booking_id: string
  reviewer_id: string
  reviewer_name: string
  reviewer_avatar_url?: string
  rating: number
  comment: string
  host_response?: string
  host_response_at?: string
  created_at: string
}

// ── Search Filters ────────────────────────────────────────────
export interface SpaceSearchFilters {
  query?: string
  space_type?: SpaceType
  min_price?: number
  max_price?: number
  min_sqm?: number
  max_sqm?: number
  power_type?: PowerType
  instant_book_only?: boolean
  has_equipment?: boolean
  sort_by?: 'price_low' | 'price_high' | 'rating' | 'newest'
  suburb?: string
}
